import { useEffect, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/20/solid';

import { Modal } from '../../../common/Atoms/Modal';
import { Button } from '../../../common/Atoms/Button';
import { Pill } from '../../../common/Atoms/Pill';
import { Heading } from '../../../common/Atoms/Typography/Heading';
import { Paragraph } from '../../../common/Atoms/Typography/Paragraph';
import { useSendEmailOnDemandMutation } from '../../services/api/communicationsApi/communicationsApi';

const ON_DEMAND_EMAIL_MIN_LENGTH = 4;
const ON_DEMAND_EMAIL_BODY_MAX_LENGTH = 10000;
const ON_DEMAND_EMAIL_SUBJECT_MAX_LENGTH = 255;

interface EmailReplyComposerProps {
  organisationId?: string;
  contactId?: number;
  defaultRecipients?: string[];
  defaultSubject?: string;
  replyToInboundEmailId?: string;
  containerClassName?: string;
  titleLabel?: string;
  bodyPlaceholder?: string;
  showSubjectInput?: boolean;
  onSent?: () => Promise<unknown> | unknown;
}

export function EmailReplyComposer({
  organisationId,
  contactId,
  defaultRecipients = [],
  defaultSubject = ``,
  replyToInboundEmailId,
  containerClassName = `mt-4 border-t border-gray-200 pt-4`,
  titleLabel = `Reply via Email`,
  bodyPlaceholder = `Type your email reply...`,
  showSubjectInput = true,
  onSent,
}: EmailReplyComposerProps) {
  const [sendEmailOnDemand, { isLoading: isSendingReply, error: sendReplyError }] = useSendEmailOnDemandMutation();
  const [subject, setSubject] = useState(defaultSubject);
  const [subjectTouched, setSubjectTouched] = useState(false);
  const [recipients, setRecipients] = useState<string[]>(defaultRecipients);
  const [recipientsTouched, setRecipientsTouched] = useState(false);
  const [replyBody, setReplyBody] = useState(``);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const defaultRecipientsKey = defaultRecipients.join(`,`);

  // Communications load asynchronously, so keep the prefilled subject in sync until the user edits it
  useEffect(() => {
    if (!subjectTouched) {
      setSubject(defaultSubject);
    }
  }, [defaultSubject, subjectTouched]);

  // Keep the prefilled recipient list in sync with the source message until the user edits it
  useEffect(() => {
    if (!recipientsTouched) {
      setRecipients(defaultRecipientsKey ? defaultRecipientsKey.split(`,`) : []);
    }
  }, [defaultRecipientsKey, recipientsTouched]);

  const hasSubject = showSubjectInput ? !!subject.trim() : !!defaultSubject.trim();
  const hasRecipients = recipients.length > 0;
  const canReply = !!organisationId
    && !!contactId
    && hasSubject
    && hasRecipients
    && replyBody.trim().length >= ON_DEMAND_EMAIL_MIN_LENGTH;
  const sendDisabledReason = !hasRecipients
    ? `At least one recipient is required`
    : ``;
  const sendReplyErrorMessage = sendReplyError
    ? ((sendReplyError as { data?: { message?: string } })?.data?.message || `Failed to send email reply`)
    : ``;

  function removeRecipient(target: string) {
    setRecipientsTouched(true);
    setRecipients(current => current.filter(email => email !== target));
  }

  async function onSendReply() {
    const subjectToSend = showSubjectInput ? subject.trim() : defaultSubject.trim();

    if (!organisationId || !contactId || !subjectToSend || !replyBody.trim() || !hasRecipients) {
      return;
    }

    await sendEmailOnDemand({
      organisationId,
      contactId,
      subject: subjectToSend,
      body: replyBody.trim(),
      toEmail: recipients.join(`,`),
      replyToInboundEmailId,
    }).unwrap();

    setReplyBody(``);
    setConfirmOpen(false);
    await onSent?.();
  }

  return (
    <>
      <div className={ containerClassName }>
        <label className={ `block text-xs font-medium text-gray-600 mb-2` }>
          { titleLabel }
        </label>
        <div className={ `mb-2` }>
          <span className={ `block text-[11px] font-medium text-gray-500 mb-1` }>
            { `To` }
          </span>
          <div className={ `flex flex-wrap items-center gap-1.5 rounded-md border border-gray-300 p-2` }>
            { recipients.length ? recipients.map(recipient => (
              <Pill
                key={ recipient }
                message={ recipient }
              >
                <button
                  type={ `button` }
                  aria-label={ `Remove ${recipient}` }
                  className={ `text-gray-400 hover:text-gray-700` }
                  onClick={ () => removeRecipient(recipient) }
                >
                  <XMarkIcon className={ `h-3.5 w-3.5` } />
                </button>
              </Pill>
            )) : (
              <span className={ `text-xs text-gray-400` }>
                { `No recipients selected` }
              </span>
            ) }
          </div>
        </div>
        { showSubjectInput ? (
          <input
            type={ `text` }
            className={ `w-full rounded-md border border-gray-300 p-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-indigo-500 mb-2` }
            value={ subject }
            maxLength={ ON_DEMAND_EMAIL_SUBJECT_MAX_LENGTH }
            onChange={ e => {
              setSubjectTouched(true);
              setSubject(e.target.value);
            } }
            placeholder={ `Subject` }
          />
        ) : null }
        <textarea
          className={ `w-full rounded-md border border-gray-300 p-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-indigo-500` }
          rows={ 3 }
          value={ replyBody }
          maxLength={ ON_DEMAND_EMAIL_BODY_MAX_LENGTH }
          onChange={ e => setReplyBody(e.target.value) }
          placeholder={ bodyPlaceholder }
        />
        <div className={ `mt-2 flex items-center justify-between` }>
          <div className={ `text-xs text-red-600` }>
            { sendReplyErrorMessage }
          </div>
          <Button
            disabled={ !canReply || isSendingReply }
            tooltip={ sendDisabledReason }
            loading={ isSendingReply }
            color={ `paidniceGreen` }
            size={ `md` }
            onClick={ () => setConfirmOpen(true) }
          >
            { `Send New Email` }
          </Button>
        </div>
      </div>

      <Modal
        open={ confirmOpen }
        setOpen={ () => setConfirmOpen(false) }
        title={ `` }
      >
        <div className={ `space-y-4 pb-2` }>
          <Heading size={ `md` }>{ `Confirm email send` }</Heading>
          <Paragraph
            size={ `sm` }
            variant={ `secondary` }
          >
            { `Please review the details before sending.` }
          </Paragraph>
          <div className={ `text-sm text-gray-900` }>
            <span className={ `font-semibold` }>{ `Recipients: ` }</span>
            <span>{ recipients.length ? recipients.join(`, `) : `-` }</span>
          </div>
          <div className={ `text-sm text-gray-900` }>
            <span className={ `font-semibold` }>{ `Subject: ` }</span>
            <span>{ (showSubjectInput ? subject.trim() : defaultSubject.trim()) || `-` }</span>
          </div>
          <div>
            <div className={ `text-sm font-semibold text-gray-900 mb-1` }>{ `Message preview` }</div>
            <div className={ `rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-900 whitespace-pre-wrap wrap-break-word` }>
              { replyBody.trim() || `-` }
            </div>
          </div>
          { sendReplyErrorMessage ? (
            <div className={ `text-sm text-red-600` }>
              { sendReplyErrorMessage }
            </div>
          ) : null }
          <div className={ `flex justify-end gap-2 pt-2` }>
            <Button
              color={ `gray` }
              size={ `md` }
              onClick={ () => setConfirmOpen(false) }
              disabled={ isSendingReply }
            >
              { `Cancel` }
            </Button>
            <Button
              color={ `paidniceGreen` }
              size={ `md` }
              loading={ isSendingReply }
              onClick={ onSendReply }
              disabled={ !canReply || isSendingReply }
            >
              { `Confirm & Send` }
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
